import React, { useState, useRef, useEffect, useCallback } from 'react';

const IslamicPatternMaker = () => {
  const [divisions, setDivisions] = useState(6);
  const [lines, setLines] = useState([]);
  const [mode, setMode] = useState('line');
  const [showConstruction, setShowConstruction] = useState(true);
  const [showTessellation, setShowTessellation] = useState(false);
  const [showCircleInTessellation, setShowCircleInTessellation] = useState(false);
  const [showGridInTessellation, setShowGridInTessellation] = useState(false);
  const [tessellationSize, setTessellationSize] = useState(3);
  const [lineStart, setLineStart] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [showSaveLoad, setShowSaveLoad] = useState(false);
  const [patternName, setPatternName] = useState('');
  const canvasRef = useRef(null);
  const tessCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const canvasSize = 400;
  const centerX = canvasSize / 2;
  const centerY = canvasSize / 2;
  const radius = 140;
  const squareSize = 320;
  const squareLeft = (canvasSize - squareSize) / 2;
  const squareTop = (canvasSize - squareSize) / 2;
  const snapDistance = 15;

  const savePatternToFile = () => {
    const name = patternName.trim() || 'my-pattern';
    const pattern = {
      name: name,
      divisions,
      lines,
      createdAt: new Date().toISOString(),
      version: '1.0'
    };
    const blob = new Blob([JSON.stringify(pattern, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/\s+/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setPatternName('');
  };

  const loadPatternFromFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const pattern = JSON.parse(event.target.result);
        if (pattern.divisions && pattern.lines) {
          setDivisions(pattern.divisions);
          setLines(pattern.lines);
          setLineStart(null);
          alert(`Loaded pattern: ${pattern.name || 'Unnamed'}`);
        } else {
          alert('Invalid pattern file');
        }
      } catch (err) {
        alert('Error reading file. Make sure it is a valid pattern JSON file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const isOnSquare = useCallback((x, y) => {
    const margin = 8;
    const onLeft = Math.abs(x - squareLeft) < margin;
    const onRight = Math.abs(x - (squareLeft + squareSize)) < margin;
    const onTop = Math.abs(y - squareTop) < margin;
    const onBottom = Math.abs(y - (squareTop + squareSize)) < margin;
    const withinX = x >= squareLeft - margin && x <= squareLeft + squareSize + margin;
    const withinY = y >= squareTop - margin && y <= squareTop + squareSize + margin;
    return (onLeft || onRight) && withinY || (onTop || onBottom) && withinX;
  }, []);

  const replicateAcrossDivisions = useCallback((item) => {
    const items = [];
    const angleStep = (2 * Math.PI) / divisions;
    
    for (let i = 0; i < divisions; i++) {
      const rotationAngle = angleStep * i;
      const cos = Math.cos(rotationAngle);
      const sin = Math.sin(rotationAngle);
      
      if (item.x1 !== undefined) {
        const dx1 = item.x1 - centerX;
        const dy1 = item.y1 - centerY;
        const dx2 = item.x2 - centerX;
        const dy2 = item.y2 - centerY;
        items.push({
          ...item,
          x1: centerX + dx1 * cos - dy1 * sin,
          y1: centerY + dx1 * sin + dy1 * cos,
          x2: centerX + dx2 * cos - dy2 * sin,
          y2: centerY + dx2 * sin + dy2 * cos,
          division: i
        });
      } else {
        const dx = item.x - centerX;
        const dy = item.y - centerY;
        items.push({
          ...item,
          x: centerX + dx * cos - dy * sin,
          y: centerY + dx * sin + dy * cos,
          division: i
        });
      }
    }
    return items;
  }, [divisions]);

  const getLineIntersection = (x1, y1, x2, y2, x3, y3, x4, y4) => {
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 0.001) return null;
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    if (t >= 0.01 && t <= 0.99 && u >= 0.01 && u <= 0.99) {
      return { x: x1 + t * (x2 - x1), y: y1 + t * (y2 - y1) };
    }
    return null;
  };

  const getAllDrawnElements = useCallback(() => {
    const allLines = [];
    
    lines.forEach(line => {
      if (line.noSymmetry) {
        allLines.push(line);
      } else {
        allLines.push(...replicateAcrossDivisions(line));
      }
    });
    
    return { allLines };
  }, [lines, replicateAcrossDivisions]);

  const getSnapPoints = useCallback(() => {
    const points = [];
    const angleStep = (2 * Math.PI) / divisions;
    
    for (let i = 0; i < divisions; i++) {
      const angle = angleStep * i - Math.PI / 2;
      for (let d = 0.1; d <= 1; d += 0.1) {
        points.push({
          x: centerX + radius * d * Math.cos(angle),
          y: centerY + radius * d * Math.sin(angle),
          type: d === 1 ? 'circumference' : 'construction',
          isCircle: true
        });
      }
    }
    
    for (let i = 0; i < divisions; i++) {
      const startAngle = angleStep * i - Math.PI / 2;
      const endAngle = angleStep * (i + 1) - Math.PI / 2;
      for (let t = 0.2; t < 1; t += 0.2) {
        const angle = startAngle + (endAngle - startAngle) * t;
        points.push({
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
          type: 'circumference',
          isCircle: true
        });
      }
    }
    
    points.push({ x: centerX, y: centerY, type: 'center', isCircle: true });
    
    const corners = [
      [squareLeft, squareTop],
      [squareLeft + squareSize, squareTop],
      [squareLeft, squareTop + squareSize],
      [squareLeft + squareSize, squareTop + squareSize]
    ];
    corners.forEach(([x, y]) => {
      points.push({ x, y, type: 'square', isCircle: false });
    });
    
    const edgePoints = 8;
    for (let i = 1; i < edgePoints; i++) {
      const t = i / edgePoints;
      points.push({ x: squareLeft + squareSize * t, y: squareTop, type: 'square', isCircle: false });
      points.push({ x: squareLeft + squareSize * t, y: squareTop + squareSize, type: 'square', isCircle: false });
      points.push({ x: squareLeft, y: squareTop + squareSize * t, type: 'square', isCircle: false });
      points.push({ x: squareLeft + squareSize, y: squareTop + squareSize * t, type: 'square', isCircle: false });
    }
    
    const { allLines } = getAllDrawnElements();
    allLines.forEach(l => {
      [0.2, 0.4, 0.5, 0.6, 0.8].forEach(t => {
        const px = l.x1 + (l.x2 - l.x1) * t;
        const py = l.y1 + (l.y2 - l.y1) * t;
        const exists = points.some(p => Math.abs(p.x - px) < 3 && Math.abs(p.y - py) < 3);
        if (!exists) {
          points.push({ x: px, y: py, type: 'user-line', isCircle: !l.noSymmetry });
        }
      });
    });
    
    for (let i = 0; i < allLines.length; i++) {
      for (let j = i + 1; j < allLines.length; j++) {
        const intersection = getLineIntersection(
          allLines[i].x1, allLines[i].y1, allLines[i].x2, allLines[i].y2,
          allLines[j].x1, allLines[j].y1, allLines[j].x2, allLines[j].y2
        );
        if (intersection) {
          const exists = points.some(p => Math.abs(p.x - intersection.x) < 3 && Math.abs(p.y - intersection.y) < 3);
          if (!exists) {
            points.push({ x: intersection.x, y: intersection.y, type: 'intersection', isCircle: false });
          }
        }
      }
    }
    
    return points;
  }, [divisions, getAllDrawnElements]);

  const findNearestSnapPoint = useCallback((x, y) => {
    const points = getSnapPoints();
    let nearest = null;
    let minDist = snapDistance;
    for (const point of points) {
      const dist = Math.sqrt((x - point.x) ** 2 + (y - point.y) ** 2);
      if (dist < minDist) {
        minDist = dist;
        nearest = point;
      }
    }
    return nearest;
  }, [getSnapPoints]);

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const nearest = findNearestSnapPoint(x, y);
    
    if (mode === 'line' && nearest) {
      if (!lineStart) {
        setLineStart({ x: nearest.x, y: nearest.y, isCircle: nearest.isCircle });
      } else {
        if (lineStart.x !== nearest.x || lineStart.y !== nearest.y) {
          const shouldHaveSymmetry = lineStart.isCircle && nearest.isCircle;
          const newLine = {
            x1: lineStart.x, y1: lineStart.y,
            x2: nearest.x, y2: nearest.y,
            id: Date.now(),
            noSymmetry: !shouldHaveSymmetry
          };
          setLines([...lines, newLine]);
        }
        setLineStart(null);
      }
    } else if (mode === 'erase') {
      let lineToRemove = null;
      for (const line of lines) {
        const linesToCheck = line.noSymmetry ? [line] : replicateAcrossDivisions(line);
        for (const l of linesToCheck) {
          if (pointToLineDistance(x, y, l.x1, l.y1, l.x2, l.y2) < 12) {
            lineToRemove = line.id;
            break;
          }
        }
        if (lineToRemove) break;
      }
      if (lineToRemove) {
        setLines(lines.filter(l => l.id !== lineToRemove));
      }
    }
  };

  const pointToLineDistance = (px, py, x1, y1, x2, y2) => {
    const A = px - x1, B = py - y1, C = x2 - x1, D = y2 - y1;
    const dot = A * C + B * D, lenSq = C * C + D * D;
    let param = lenSq !== 0 ? dot / lenSq : -1;
    let xx = param < 0 ? x1 : param > 1 ? x2 : x1 + param * C;
    let yy = param < 0 ? y1 : param > 1 ? y2 : y1 + param * D;
    return Math.sqrt((px - xx) ** 2 + (py - yy) ** 2);
  };

  const handleCanvasMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    setHoveredPoint(findNearestSnapPoint(e.clientX - rect.left, e.clientY - rect.top));
  };

  const drawMainCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasSize, canvasSize);
    ctx.fillStyle = '#fefefe';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    
    ctx.strokeStyle = '#059669';
    ctx.lineWidth = 2;
    ctx.strokeRect(squareLeft, squareTop, squareSize, squareSize);
    
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();
    
    if (showConstruction) {
      ctx.strokeStyle = '#bfdbfe';
      ctx.lineWidth = 1;
      const angleStep = (2 * Math.PI) / divisions;
      for (let i = 0; i < divisions; i++) {
        const angle = angleStep * i - Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle));
        ctx.stroke();
      }
      
      const snapPoints = getSnapPoints();
      snapPoints.forEach(point => {
        if (point.type === 'user-line') {
          ctx.fillStyle = '#86efac';
          ctx.beginPath();
          ctx.arc(point.x, point.y, 3, 0, 2 * Math.PI);
          ctx.fill();
        } else if (point.type === 'intersection') {
          ctx.fillStyle = '#c4b5fd';
          ctx.beginPath();
          ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
          ctx.fill();
        } else if (point.type === 'square') {
          ctx.fillStyle = '#6ee7b7';
          ctx.beginPath();
          ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
          ctx.fill();
        } else {
          ctx.strokeStyle = '#cbd5e1';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(point.x, point.y, 3, 0, 2 * Math.PI);
          ctx.stroke();
        }
      });
    }
    
    if (hoveredPoint && (mode === 'dot' || mode === 'line')) {
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath();
      ctx.arc(hoveredPoint.x, hoveredPoint.y, 8, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    
    const { allLines } = getAllDrawnElements();
    
    allLines.forEach(l => {
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(l.x1, l.y1);
      ctx.lineTo(l.x2, l.y2);
      ctx.stroke();
    });
    
    if (lineStart) {
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(lineStart.x, lineStart.y, 8, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [divisions, showConstruction, hoveredPoint, lineStart, getSnapPoints, getAllDrawnElements, mode]);

  const drawTessellation = useCallback(() => {
    const canvas = tessCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const tileSize = 100;
    const cols = tessellationSize;
    const rows = tessellationSize;
    canvas.width = cols * tileSize;
    canvas.height = rows * tileSize;
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const { allLines } = getAllDrawnElements();
    const scale = tileSize / squareSize;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        ctx.save();
        
        const flipH = col % 2 === 1;
        const flipV = row % 2 === 1;
        
        ctx.translate(col * tileSize, row * tileSize);
        
        if (flipH && flipV) {
          ctx.translate(tileSize, tileSize);
          ctx.scale(-scale, -scale);
        } else if (flipH) {
          ctx.translate(tileSize, 0);
          ctx.scale(-scale, scale);
        } else if (flipV) {
          ctx.translate(0, tileSize);
          ctx.scale(scale, -scale);
        } else {
          ctx.scale(scale, scale);
        }
        
        ctx.beginPath();
        ctx.rect(0, 0, squareSize, squareSize);
        ctx.clip();
        
        allLines.forEach(l => {
          ctx.strokeStyle = '#1e3a5f';
          ctx.lineWidth = 2 / scale;
          ctx.beginPath();
          ctx.moveTo(l.x1 - squareLeft, l.y1 - squareTop);
          ctx.lineTo(l.x2 - squareLeft, l.y2 - squareTop);
          ctx.stroke();
        });
        
        if (showCircleInTessellation) {
          ctx.strokeStyle = '#1e3a5f';
          ctx.lineWidth = 1.5 / scale;
          ctx.beginPath();
          ctx.arc(centerX - squareLeft, centerY - squareTop, radius, 0, 2 * Math.PI);
          ctx.stroke();
        }
        
        if (showGridInTessellation) {
          const angleStep = (2 * Math.PI) / divisions;
          
          ctx.strokeStyle = '#94a3b8';
          ctx.lineWidth = 1 / scale;
          for (let i = 0; i < divisions; i++) {
            const angle = angleStep * i - Math.PI / 2;
            ctx.beginPath();
            ctx.moveTo(centerX - squareLeft, centerY - squareTop);
            ctx.lineTo(
              centerX - squareLeft + radius * Math.cos(angle),
              centerY - squareTop + radius * Math.sin(angle)
            );
            ctx.stroke();
          }
          
          ctx.fillStyle = '#94a3b8';
          for (let i = 0; i < divisions; i++) {
            const angle = angleStep * i - Math.PI / 2;
            for (let d = 0.1; d <= 1; d += 0.1) {
              ctx.beginPath();
              ctx.arc(
                centerX - squareLeft + radius * d * Math.cos(angle),
                centerY - squareTop + radius * d * Math.sin(angle),
                2 / scale, 0, 2 * Math.PI
              );
              ctx.fill();
            }
          }
          
          for (let i = 0; i < divisions; i++) {
            const startAngle = angleStep * i - Math.PI / 2;
            const endAngle = angleStep * (i + 1) - Math.PI / 2;
            for (let t = 0.2; t < 1; t += 0.2) {
              const angle = startAngle + (endAngle - startAngle) * t;
              ctx.beginPath();
              ctx.arc(
                centerX - squareLeft + radius * Math.cos(angle),
                centerY - squareTop + radius * Math.sin(angle),
                2 / scale, 0, 2 * Math.PI
              );
              ctx.fill();
            }
          }
          
          ctx.beginPath();
          ctx.arc(centerX - squareLeft, centerY - squareTop, 3 / scale, 0, 2 * Math.PI);
          ctx.fill();
          
          const edgePoints = 8;
          for (let i = 0; i <= edgePoints; i++) {
            const t = i / edgePoints;
            ctx.beginPath();
            ctx.arc(squareSize * t, 0, 2 / scale, 0, 2 * Math.PI);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(squareSize * t, squareSize, 2 / scale, 0, 2 * Math.PI);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(0, squareSize * t, 2 / scale, 0, 2 * Math.PI);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(squareSize, squareSize * t, 2 / scale, 0, 2 * Math.PI);
            ctx.fill();
          }
        }
        
        ctx.restore();
      }
    }
  }, [tessellationSize, getAllDrawnElements, showCircleInTessellation, showGridInTessellation, divisions]);

  useEffect(() => { drawMainCanvas(); }, [drawMainCanvas]);
  useEffect(() => { if (showTessellation) drawTessellation(); }, [showTessellation, drawTessellation]);

  const clearAll = () => { setLines([]); setLineStart(null); };
  const handleDivisionChange = (n) => { setDivisions(n); clearAll(); };

  const printTessellation = () => {
    const canvas = tessCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>Islamic Pattern - ${divisions}-fold</title></head>
      <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;">
      <img src="${dataUrl}" style="max-width:100%;max-height:100vh;" onload="window.print();"/></body></html>`);
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-center text-indigo-900 mb-1">Islamic Geometric Pattern Maker</h1>
        <p className="text-center text-indigo-700 mb-3 text-sm">Create symmetric patterns with seamless tessellation</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-wrap gap-3 mb-3 items-center">
              <select value={divisions} onChange={(e) => handleDivisionChange(Number(e.target.value))}
                className="border rounded px-2 py-1 text-sm bg-white">
                {[4,5,6,8,10,12].map(n => <option key={n} value={n}>{n}-fold</option>)}
              </select>
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={showConstruction} onChange={(e) => setShowConstruction(e.target.checked)} className="rounded"/>
                Grid
              </label>
              <button onClick={clearAll} className="px-3 py-1 bg-gray-500 text-white rounded text-sm">Clear</button>
              <button onClick={() => setShowSaveLoad(!showSaveLoad)} className="px-3 py-1 bg-purple-600 text-white rounded text-sm">
                {showSaveLoad ? 'Hide' : 'Save/Load'}
              </button>
            </div>
            
            {showSaveLoad && (
              <div className="mb-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={patternName}
                    onChange={(e) => setPatternName(e.target.value)}
                    placeholder="Pattern name..."
                    className="flex-1 border rounded px-2 py-1 text-sm"
                  />
                  <button onClick={savePatternToFile} className="px-3 py-1 bg-green-600 text-white rounded text-sm">
                    💾 Save File
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={loadPatternFromFile}
                    accept=".json"
                    className="hidden"
                  />
                  <button 
                    onClick={() => fileInputRef.current.click()} 
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                  >
                    📂 Load File
                  </button>
                  <span className="text-xs text-gray-500 self-center">Load a .json pattern file</span>
                </div>
              </div>
            )}
            
            <div className="flex gap-2 mb-3">
              {['line', 'erase'].map(m => (
                <button key={m} onClick={() => { setMode(m); setLineStart(null); }}
                  className={`px-3 py-1 rounded text-sm font-medium ${mode === m 
                    ? (m === 'erase' ? 'bg-orange-500' : 'bg-blue-600') + ' text-white' 
                    : 'bg-gray-100'}`}>
                  {m === 'line' ? '─ Line' : '✕ Erase'}
                </button>
              ))}
            </div>
            
            <canvas ref={canvasRef} width={canvasSize} height={canvasSize}
              onClick={handleCanvasClick} onMouseMove={handleCanvasMove} onMouseLeave={() => setHoveredPoint(null)}
              className="border-2 border-indigo-200 rounded-lg cursor-crosshair mx-auto block bg-white"/>
            
            <div className="mt-2 text-sm text-gray-600 text-center">
              {mode === 'line' && !lineStart && 'Click a point to start a line'}
              {mode === 'line' && lineStart && 'Click another point to complete the line'}
              {mode === 'erase' && 'Click on a line to remove it'}
            </div>
            
            <div className="mt-2 flex flex-wrap gap-3 justify-center text-xs text-gray-600">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full border border-gray-400"></span> Circle (symmetric)</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-300"></span> Square (no symmetry)</span>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-wrap gap-3 mb-3 items-center">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={showTessellation} onChange={(e) => setShowTessellation(e.target.checked)} className="rounded w-4 h-4"/>
                <span className="font-medium">Tessellation</span>
              </label>
              {showTessellation && (
                <>
                  <select value={tessellationSize} onChange={(e) => setTessellationSize(Number(e.target.value))}
                    className="border rounded px-2 py-1 text-sm bg-white">
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}x{n}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={showCircleInTessellation} onChange={(e) => setShowCircleInTessellation(e.target.checked)} className="rounded"/>
                    Circle
                  </label>
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={showGridInTessellation} onChange={(e) => setShowGridInTessellation(e.target.checked)} className="rounded"/>
                    Grid
                  </label>
                  <button onClick={printTessellation} className="px-3 py-1 bg-green-600 text-white rounded text-sm">🖨 Print</button>
                </>
              )}
            </div>
            
            {showTessellation ? (
              <div className="border-2 border-indigo-200 rounded-lg overflow-auto max-h-80 bg-white">
                <canvas ref={tessCanvasRef} className="mx-auto block"/>
              </div>
            ) : (
              <div className="border-2 border-dashed border-indigo-200 rounded-lg h-48 flex items-center justify-center text-gray-400">
                Enable tessellation to preview
              </div>
            )}
            
            <div className="mt-3 bg-indigo-50 rounded-lg p-3 text-sm">
              <h3 className="font-semibold text-indigo-800 mb-1">How It Works:</h3>
              <ul className="text-gray-700 space-y-1 text-xs">
                <li>• Lines <strong>inside the circle</strong> repeat with radial symmetry</li>
                <li>• Lines connecting to the <strong>green square</strong> do NOT repeat</li>
                <li>• Tessellation <strong>mirrors</strong> tiles so edges match automatically!</li>
                <li>• <strong>Save</strong> your pattern as a file to keep or share</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IslamicPatternMaker;
